import { useState, useEffect, useCallback } from 'react';
import {
  IconReceipt,
  IconSend,
  IconLoader2,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { useAuth } from '../hooks/useAuth';
import { formatDateTime } from '../utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface Distributor {
  table: string;
  iCustomer: number;
  name: string;
}

interface SaleItem {
  product_name: string;
  product_price: number;
  quantity: number;
  total: number;
}

interface PurchaseOrderResult {
  distributor: string;
  iCustomer: number;
  sales: SaleItem[];
  total: number;
  totalLessVAT: number;
  charges: number;
  poAmount: number;
  poLink: string | null;
  purchaseOrderId: string | null;
}

interface InvoiceResult {
  distributor: string;
  iCustomer: number;
  invoiceId: string | null;
  invoiceNumber: string | null;
  invoiceLink: string | null;
  lineItems: Array<{ product_name: string; revenue: number }>;
  total: number;
}

interface InvoiceHistoryItem {
  xero_invoice_id: string | null;
  xero_invoice_number: string | null;
  xero_invoice_paid: boolean;
  i_customer_distributor: number;
  company_name: string;
  created_at: string;
  total: number;
  line_items: Array<{ id: number; reference: string; debit: number }>;
}

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export function ShopifyMonthlyBillingPage() {
  const { hasWriteAccess } = useAuth();
  const canWrite = hasWriteAccess('shopify');

  const [tabValue, setTabValue] = useState('po');
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [loadingDistributors, setLoadingDistributors] = useState(true);

  // PO state
  const [poDistributor, setPoDistributor] = useState<number | ''>('');
  const [poYear, setPoYear] = useState(currentYear);
  const [poMonth, setPoMonth] = useState(currentMonth);
  const [poProcessing, setPoProcessing] = useState(false);
  const [poResult, setPoResult] = useState<PurchaseOrderResult | null>(null);
  const [poError, setPoError] = useState('');
  const [poConfirmOpen, setPoConfirmOpen] = useState(false);

  // Invoice state
  const [invDistributor, setInvDistributor] = useState<number | ''>('');
  const [invYear, setInvYear] = useState(currentYear);
  const [invMonth, setInvMonth] = useState(currentMonth);
  const [invProcessing, setInvProcessing] = useState(false);
  const [invResult, setInvResult] = useState<InvoiceResult | null>(null);
  const [invError, setInvError] = useState('');
  const [invConfirmOpen, setInvConfirmOpen] = useState(false);

  // Invoice history state
  const [invoiceHistory, setInvoiceHistory] = useState<InvoiceHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<number | ''>('');
  const [historyError, setHistoryError] = useState('');

  const loadDistributors = useCallback(async () => {
    try {
      const res = await api.get('/shopify/billing/distributors');
      setDistributors(res.data.data || []);
    } catch (err: any) {
      console.error('Failed to load distributors:', err);
    } finally {
      setLoadingDistributors(false);
    }
  }, []);

  const loadInvoiceHistory = useCallback(async (filterCustomer?: number | '') => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const params: any = {};
      const filter = filterCustomer !== undefined ? filterCustomer : historyFilter;
      if (filter) params.iCustomer = filter;
      const res = await api.get('/shopify/billing/invoices', { params });
      setInvoiceHistory(res.data.data || []);
    } catch (err: any) {
      setHistoryError(err.response?.data?.message || 'Failed to load invoice history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilter]);

  useEffect(() => {
    void loadDistributors();
  }, [loadDistributors]);

  useEffect(() => {
    void loadInvoiceHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial history load; filter changes call loadInvoiceHistory with explicit customer id from Select
  }, []);

  const handleProcessPO = async () => {
    setPoConfirmOpen(false);
    setPoProcessing(true);
    setPoError('');
    setPoResult(null);

    try {
      const res = await api.post('/shopify/billing/purchase-orders/process', {
        iCustomer: poDistributor,
        year: poYear,
        month: poMonth,
      });
      setPoResult(res.data.data);
    } catch (err: any) {
      setPoError(err.response?.data?.message || 'Failed to process purchase order');
    } finally {
      setPoProcessing(false);
    }
  };

  const handleProcessInvoice = async () => {
    setInvConfirmOpen(false);
    setInvProcessing(true);
    setInvError('');
    setInvResult(null);

    try {
      const res = await api.post('/shopify/billing/invoices/process', {
        iCustomer: invDistributor,
        year: invYear,
        month: invMonth,
      });
      setInvResult(res.data.data);
      loadInvoiceHistory();
    } catch (err: any) {
      setInvError(err.response?.data?.message || 'Failed to process invoice');
    } finally {
      setInvProcessing(false);
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const selectedPoDistName = distributors.find(d => d.iCustomer === poDistributor)?.name || '';
  const selectedInvDistName = distributors.find(d => d.iCustomer === invDistributor)?.name || '';

  if (loadingDistributors) {
    return (
      <div className="flex justify-center p-4">
        <IconLoader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Monthly Billing"
        subtitle="Process Shopify purchase orders and license invoices for distributors"
      />

      <MainCard>
        <Tabs value={tabValue} onValueChange={setTabValue}>
          <TabsList variant="line" className="mb-4 w-full min-h-12 justify-start rounded-none border-0 bg-transparent px-0">
            <TabsTrigger value="po" className="gap-1.5 text-sm">
              <IconReceipt className="size-4" />
              Purchase Orders
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-1.5 text-sm">
              <IconSend className="size-4" />
              License Invoices
            </TabsTrigger>
          </TabsList>

          {/* Purchase Orders Tab */}
          <TabsContent value="po" className="mt-0 pt-2">
            <div className="flex flex-col gap-3">
              <Card>
                <CardHeader>
                  <CardTitle>Process Purchase Order</CardTitle>
                  <CardDescription>
                    Query BigQuery for Shopify sales and create a Xero purchase order for the selected distributor.
                    The billing period runs from the 25th of the previous month to the 24th of the selected month.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex flex-col gap-1.5">
                      <Label>Distributor</Label>
                      <Select
                        value={poDistributor === '' ? undefined : String(poDistributor)}
                        onValueChange={(val) => setPoDistributor(Number(val))}
                      >
                        <SelectTrigger className="min-w-[220px]" size="sm">
                          <SelectValue placeholder="Select distributor" />
                        </SelectTrigger>
                        <SelectContent>
                          {distributors.map((d) => (
                            <SelectItem key={d.iCustomer} value={String(d.iCustomer)}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>Year</Label>
                      <Select
                        value={String(poYear)}
                        onValueChange={(val) => setPoYear(Number(val))}
                      >
                        <SelectTrigger className="min-w-[120px]" size="sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>Month</Label>
                      <Select
                        value={String(poMonth)}
                        onValueChange={(val) => setPoMonth(Number(val))}
                      >
                        <SelectTrigger className="min-w-[150px]" size="sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map((m, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={() => setPoConfirmOpen(true)}
                      disabled={!canWrite || !poDistributor || poProcessing}
                    >
                      {poProcessing ? <IconLoader2 className="size-4 animate-spin" /> : <IconReceipt className="size-4" />}
                      {poProcessing ? 'Processing...' : 'Process PO'}
                    </Button>
                  </div>

                  {!canWrite && (
                    <Alert className="mt-2 border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400">
                      <IconAlertTriangle className="size-4" />
                      <AlertDescription>
                        You need write access to the Shopify page to process billing.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {poError && (
                <Alert variant="destructive">
                  <AlertDescription>{poError}</AlertDescription>
                </Alert>
              )}

              {poResult && (
                <Card>
                  <CardHeader>
                    <CardTitle>Result: {poResult.distributor}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3 flex flex-wrap gap-2">
                      <Badge variant="secondary">Total: R{poResult.total.toFixed(2)}</Badge>
                      <Badge variant="secondary">Less VAT: R{poResult.totalLessVAT.toFixed(2)}</Badge>
                      <Badge variant="warning">
                        Charges (4%): R{poResult.charges.toFixed(2)}
                      </Badge>
                      <Badge variant="success">
                        PO Amount: R{poResult.poAmount.toFixed(2)}
                      </Badge>
                    </div>

                    {poResult.poLink && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mb-3"
                        render={<a href={poResult.poLink} target="_blank" rel="noopener noreferrer" />}
                      >
                        View PO PDF
                      </Button>
                    )}

                    {poResult.sales.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {poResult.sales.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{item.product_name}</TableCell>
                              <TableCell className="text-right">{item.quantity}</TableCell>
                              <TableCell className="text-right">R{item.product_price.toFixed(2)}</TableCell>
                              <TableCell className="text-right">R{item.total.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    {poResult.sales.length === 0 && (
                      <Alert>
                        <IconInfoCircle className="size-4" />
                        <AlertDescription>No sales found for this period.</AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* License Invoices Tab */}
          <TabsContent value="invoices" className="mt-0 pt-2">
            <div className="flex flex-col gap-3">
              {/* Invoice History */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Invoice History</CardTitle>
                    <div className="flex flex-col gap-1.5">
                      <Select
                        value={historyFilter === '' ? 'all' : String(historyFilter)}
                        onValueChange={(val) => {
                          const v = val === 'all' ? '' as const : Number(val);
                          setHistoryFilter(v);
                          loadInvoiceHistory(v);
                        }}
                      >
                        <SelectTrigger className="min-w-[200px]" size="sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Distributors</SelectItem>
                          {distributors.map((d) => (
                            <SelectItem key={d.iCustomer} value={String(d.iCustomer)}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {historyError && (
                    <Alert variant="destructive" className="mb-3">
                      <AlertDescription>{historyError}</AlertDescription>
                    </Alert>
                  )}

                  {historyLoading ? (
                    <div className="flex justify-center p-3">
                      <IconLoader2 className="size-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : invoiceHistory.length === 0 ? (
                    <Alert>
                      <IconInfoCircle className="size-4" />
                      <AlertDescription>No invoice history found.</AlertDescription>
                    </Alert>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Distributor</TableHead>
                          <TableHead>Invoice Number</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Items</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoiceHistory.map((inv, idx) => (
                          <TableRow key={inv.xero_invoice_id || idx}>
                            <TableCell>{inv.created_at ? formatDateTime(inv.created_at) : '-'}</TableCell>
                            <TableCell>{inv.company_name}</TableCell>
                            <TableCell>{inv.xero_invoice_number || '-'}</TableCell>
                            <TableCell className="text-right">R{inv.total.toFixed(2)}</TableCell>
                            <TableCell>
                              <Badge variant={inv.xero_invoice_paid ? 'success' : 'warning'}>
                                {inv.xero_invoice_paid ? 'Paid' : 'Unpaid'}
                              </Badge>
                            </TableCell>
                            <TableCell>{inv.line_items?.length || 0} item(s)</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Raise Invoice */}
              <Card>
                <CardHeader>
                  <CardTitle>Raise License Invoice</CardTitle>
                  <CardDescription>
                    Create a Xero invoice for the selected distributor's Shopify license and addon fees.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex flex-col gap-1.5">
                      <Label>Distributor</Label>
                      <Select
                        value={invDistributor === '' ? undefined : String(invDistributor)}
                        onValueChange={(val) => setInvDistributor(Number(val))}
                      >
                        <SelectTrigger className="min-w-[220px]" size="sm">
                          <SelectValue placeholder="Select distributor" />
                        </SelectTrigger>
                        <SelectContent>
                          {distributors.map((d) => (
                            <SelectItem key={d.iCustomer} value={String(d.iCustomer)}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>Year</Label>
                      <Select
                        value={String(invYear)}
                        onValueChange={(val) => setInvYear(Number(val))}
                      >
                        <SelectTrigger className="min-w-[120px]" size="sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>Month</Label>
                      <Select
                        value={String(invMonth)}
                        onValueChange={(val) => setInvMonth(Number(val))}
                      >
                        <SelectTrigger className="min-w-[150px]" size="sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map((m, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={() => setInvConfirmOpen(true)}
                      disabled={!canWrite || !invDistributor || invProcessing}
                    >
                      {invProcessing ? <IconLoader2 className="size-4 animate-spin" /> : <IconSend className="size-4" />}
                      {invProcessing ? 'Processing...' : 'Raise Invoice'}
                    </Button>
                  </div>

                  {!canWrite && (
                    <Alert className="mt-2 border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400">
                      <IconAlertTriangle className="size-4" />
                      <AlertDescription>
                        You need write access to the Shopify page to raise invoices.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {invError && (
                <Alert variant="destructive">
                  <AlertDescription>{invError}</AlertDescription>
                </Alert>
              )}

              {invResult && (
                <Card>
                  <CardHeader>
                    <CardTitle>Result: {invResult.distributor}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3 flex flex-wrap gap-2">
                      {invResult.invoiceNumber && (
                        <Badge>Invoice: {invResult.invoiceNumber}</Badge>
                      )}
                      <Badge variant="success">
                        Total: R{invResult.total.toFixed(2)}
                      </Badge>
                    </div>

                    {invResult.invoiceLink && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mb-3"
                        render={<a href={invResult.invoiceLink} target="_blank" rel="noopener noreferrer" />}
                      >
                        View Invoice PDF
                      </Button>
                    )}

                    {invResult.lineItems.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">Revenue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invResult.lineItems.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{item.product_name}</TableCell>
                              <TableCell className="text-right">R{item.revenue.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </MainCard>

      {/* PO Confirmation Dialog */}
      <Dialog
        open={poConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setPoConfirmOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Purchase Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to process a Shopify purchase order for{' '}
              <strong>{selectedPoDistName}</strong> for{' '}
              <strong>{months[poMonth - 1]} {poYear}</strong>?
              <br /><br />
              This will query BigQuery for sales, create a Xero PO, and email the distributor contacts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleProcessPO}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Confirmation Dialog */}
      <Dialog
        open={invConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setInvConfirmOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to raise a Shopify license invoice for{' '}
              <strong>{selectedInvDistName}</strong> for{' '}
              <strong>{months[invMonth - 1]} {invYear}</strong>?
              <br /><br />
              This will create a Xero invoice, record transactions, and email the distributor contacts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleProcessInvoice}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
